const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api';

/**
 * Opens a connection to the API origin ahead of the first chat message.
 *
 * This replaces the TARS "wake-up" service, which existed to mask Render
 * free-tier cold starts (30-60s, retried with backoff) and to hold the chat
 * UI behind a spinner until the Flask app reported its services initialized.
 * The backend is a Cloudflare Worker now — no cold start, nothing to wake —
 * so nothing gates on this call and its result is ignored.
 *
 * It still earns the request: api.parthkohale.com is a different origin from
 * the site itself, so the first call there pays a fresh DNS + TCP + TLS
 * handshake. Measured against the deployed Worker, that first call takes
 * ~620-665ms end to end versus ~125-150ms once the connection exists.
 *
 * The saving only holds if the socket survives until the user actually sends
 * something, so that was checked rather than assumed: repeat calls from the
 * page after 3s and 15s idle gaps came back in 187ms and 211ms, i.e. still
 * warm well past the terminal intro plus the time it takes to open the chat.
 *
 * It does not remove the CORS preflight on POST /chat/stream, which still
 * costs a round trip of its own because that request carries a Content-Type
 * header.
 *
 * Deliberately a bare GET with no headers: setting Content-Type here would
 * make this a non-simple request and trigger a preflight, doubling the cost
 * of the thing meant to save one. /health is the rate-limit-exempt endpoint.
 */
export async function prewarmApiConnection(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/health`);
  } catch {
    // Best effort, and a failure here says nothing worth acting on: the chat
    // reports a real outage when the user actually sends a message.
  }
}
